import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useAdminImpersonation } from '@/hooks/useAdminImpersonation';
import { useAdminRole } from '@/hooks/useAdminRole';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  type: 'company' | 'association' | 'federation' | 'individual';
  siret: string | null;
  rna_number: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  website: string | null;
  description: string | null;
  logo_url: string | null;
  verification_status: 'pending' | 'under_review' | 'approved' | 'rejected';
  verification_document_url: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
  president_name: string | null;
  president_id_document_url: string | null;
  rib_document_url: string | null;
  iban: string | null;
  bic: string | null;
  cgv_accepted: boolean | null;
  cgv_accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

// Fonction utilitaire pour créer un slug
const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9]+/g, '-')     // Remplace caractères spéciaux par -
    .replace(/^-+|-+$/g, '')          // Supprime les - en début/fin
    .substring(0, 50);                // Limite la longueur
};

// Génère un slug unique en vérifiant la base de données
const generateUniqueSlug = async (name: string): Promise<string> => {
  const baseSlug = slugify(name) || 'organisation';
  let slug = baseSlug;
  let counter = 1;

  while (true) {
    const { data } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();

    if (!data) break; // Slug disponible
    
    counter++;
    slug = `${baseSlug}-${counter}`;
  }

  return slug;
};

export function useOrganization() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { impersonatedOrgId } = useAdminImpersonation();
  const { isAdmin, isLoading: isAdminLoading } = useAdminRole();

  const { data: organization, isLoading, error } = useQuery({
    queryKey: ['organization', user?.id, impersonatedOrgId, isAdmin],
    queryFn: async () => {
      if (!user) return null;

      // Admin impersonation: load the targeted organization directly.
      // Super-admin RLS policy "Super admins can view all organizations" allows this.
      if (impersonatedOrgId && isAdmin) {
        const { data: org, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', impersonatedOrgId)
          .single();

        if (orgError) throw orgError;
        return org as Organization;
      }

      const { data: membership, error: membershipError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipError) throw membershipError;
      if (!membership) return null;

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', membership.organization_id)
        .single();

      if (orgError) throw orgError;
      return org as Organization;
    },
    // Wait for admin role to resolve before firing — avoids briefly running
    // the membership fallback while impersonating as a super-admin.
    enabled: !!user && !(impersonatedOrgId && isAdminLoading),
  });

  const createOrganization = useMutation({
    mutationFn: async (orgData: Partial<Organization>) => {
      if (!user) throw new Error('Non authentifié');

      // Generate UUID client-side to avoid needing RETURNING (blocked by RLS before membership exists)
      const orgId = crypto.randomUUID();

      // Generate a unique slug from the organization name
      const slug = await generateUniqueSlug(orgData.name || 'organisation');

      // Step 1: Insert organization without .select() (avoids RLS SELECT check)
      const { error: orgError } = await supabase
        .from('organizations')
        .insert({ ...orgData, id: orgId, slug } as any);

      if (orgError) throw orgError;

      // Step 2: Add user as owner (now RLS SELECT will pass)
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: orgId,
          user_id: user.id,
          role: 'owner',
        });

      if (memberError) throw memberError;

      // Step 3: organizer role is now auto-assigned by DB trigger on organization_members insert

      // Step 4: Now fetch the created organization (RLS passes thanks to membership)
      const { data: org, error: fetchError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (fetchError) throw fetchError;

      return org;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast({
        title: 'Organisation créée',
        description: 'Votre organisation a été créée avec succès. Elle est en attente de vérification.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateOrganization = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Organization> & { id: string }) => {
      const { data: org, error } = await supabase
        .from('organizations')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return org;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast({
        title: 'Organisation mise à jour',
        description: 'Les informations ont été enregistrées.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    organization,
    isLoading,
    error,
    createOrganization,
    updateOrganization,
  };
}
